import { PlexUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import {
  CircularProgress,
  createStyles,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Theme,
} from '@material-ui/core';
import React, { ChangeEvent, Fragment, SyntheticEvent } from 'react';

import { CardType, useGetBoardsNamesQuery } from '../../../../graphql/gen/types';
import { colorPalette, spacings } from '../../../../theme';
import { Entities } from '../../../shared/slate/entities';
import { SlateEditor, SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { LabelInput } from '../../common/LabelInput';
import { PlexName } from '../../common/PlexName';
import { canChangeQuestionBoard } from '../../permissions';
import { DialogTopBar } from '../DialogTopBar';
import { CreateOrEditDialogTitle } from '../post/CreateOrEditDialogTitle';

type AnswerSlate = {
  body: SlateEditorValue;
  primaryLinkId: string | null | undefined;
  entities: Entities;
};

export type State = {
  text: string;
  boardId: string;
  labelIds: string[];
  answer?: AnswerSlate;
};

type Props = {
  plexName?: string;
  plexId: string;
  plexColor?: string | null;
  role: PlexUserRelationState;
  value: State;
  onChange: (value: State) => void;
  title: string;
  type: CardType;
  answer?: AnswerSlate;
};

export function FactDialogContent({
  plexName,
  plexId,
  plexColor,
  value,
  title,
  onChange,
  role,
  answer,
  type,
}: Props): React.ReactElement {
  const classes = useStyles();
  const handleTextChange = (event: SyntheticEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    onChange({
      ...value,
      text: event.currentTarget.value,
    });
  };

  const { data: boardsData, loading: loadingBoards } = useGetBoardsNamesQuery({
    variables: { plexId },
  });

  const handleChangeLabelIds = (labelIds: string[]): void => {
    onChange({ ...value, labelIds });
  };

  const handleBoardChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>): void => {
    const boardId = event.target.value as string;
    onChange({ ...value, boardId });
  };

  const handleBodyChange = (body: SlateEditorValue): void => {
    if (value.answer)
      onChange({
        ...value,
        answer: {
          ...value.answer,
          body,
        },
      });
  };

  const handleChangePrimaryLink = (primaryLinkId: string): void => {
    if (value.answer)
      onChange({
        ...value,
        answer: {
          ...value.answer,
          primaryLinkId,
        },
      });
  };

  if (loadingBoards) {
    return <CircularProgress size={24} />;
  }

  return (
    <Fragment>
      <DialogTopBar>
        {plexName && <PlexName color={plexColor} name={plexName} className={classes.plexName} />}
      </DialogTopBar>

      <CreateOrEditDialogTitle />

      <div className={classes.inputsContainer}>
        <TextField
          multiline
          fullWidth
          type={'text'}
          value={value.text}
          margin={'dense'}
          label={type === CardType.Faq ? 'Your question' : 'Card title'}
          onChange={handleTextChange}
          rowsMax={3}
        />
        <LabelInput selectedLabelIds={value.labelIds} onLabelsListChanged={handleChangeLabelIds} />

        {canChangeQuestionBoard(role) && (
          <div className={classes.boardSelectorContainer}>
            <InputLabel id="boardSelector">Board</InputLabel>
            <Select labelId="boardSelector" id="boardSelector" value={value.boardId} onChange={handleBoardChange}>
              {boardsData?.loggedIn?.plexById?.boards?.map((b) => (
                <MenuItem value={b.id} key={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
      </div>

      {value.answer && (
        <SlateEditor
          value={value.answer.body}
          initialEntities={value.answer.entities}
          onChange={handleBodyChange}
          title={type === CardType.Faq ? 'Type your answer here' : 'Enter card body'}
          primaryLink={value.answer.primaryLinkId}
          onChangePrimaryLink={handleChangePrimaryLink}
        />
      )}
    </Fragment>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
    rootTitle: {
      fontWeight: 'bold',
      marginLeft: spacings.xxlarge,
      marginTop: spacings.xlarge,
      marginBottom: spacings.xlarge,
    },
    rootTitleIcon: {
      width: 32,
      height: 32,
      marginRight: spacings.normal,
      fill: `${colorPalette.dark._800} !important`, // sets the svg color
    },
    inputsContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: spacings.small,
      paddingBottom: spacings.xlarge,
      paddingLeft: spacings.xlarge,
      paddingRight: spacings.xlarge,
      marginBottom: spacings.normal,
      flex: '1 1 auto',
      flexShrink: 0,
      [`overflow-y`]: 'auto',
      borderBottom: `1px solid ${colorPalette.gray._300}`,
    },
    spacer: {
      marginTop: spacings.xxlarge,
    },
    inputLink: {
      marginTop: 0,
    },
    boardSelectorContainer: {
      marginTop: spacings.normal,
    },
  })
);
